import React from 'react';
import type { UseFormRegister, UseFormSetValue, UseFormWatch, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { LabelInput } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { FormLayout, PermissionGuard } from '@/components/layout';
import { useTranslation } from '@/i18n';
import type { CreateEmployeeContractFormData } from '@/schemas/employee/employeeContractCreate.schema';
import {
  normalizeNumericSelectValue,
  normalizeWatchValue,
  normalizeWatchValueToString,
  normalizeSelectValue,
  normalizeSelectToNumber,
} from '@/utils/formHelpers';


interface CreateEmployeeContractFormProps {
  contractForm: {
    register: UseFormRegister<CreateEmployeeContractFormData>;
    setValue: UseFormSetValue<CreateEmployeeContractFormData>;
    watch: UseFormWatch<CreateEmployeeContractFormData>;
    errors: FieldErrors<CreateEmployeeContractFormData>;
    trigger: UseFormTrigger<CreateEmployeeContractFormData>;
  };
  contractStatusOptions: { value: string; label: string }[];
  contractTypeOptions: { value: string; label: string }[];
  contractModalityOptions: { value: string; label: string }[];
  companyOptions: { value: string; label: string }[];
  areaOptions: { value: string; label: string }[];
  positionOptions: { value: string; label: string }[];
  costCenterOptions: { value: string; label: string }[];
  unionOptions: { value: string; label: string }[];
  creating: boolean;
  onCancel: () => void;
  onSave: () => void;
}

export const CreateEmployeeContractForm: React.FC<CreateEmployeeContractFormProps> = ({
  contractForm,
  contractStatusOptions,
  contractTypeOptions,
  contractModalityOptions,
  companyOptions,
  areaOptions,
  positionOptions,
  costCenterOptions,
  unionOptions,
  creating,
  onCancel,
  onSave,
}) => {
  const { t: translate } = useTranslation();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  return (
    <FormLayout onSubmit={handleSubmit} columns={4}>
      {/* Sección 1: Datos del contrato */}
      <FormLayout.Section title="Datos del contrato">
        <Selector
          label={translate('employee:contracts.fields.contractStatus')}
          required
          options={contractStatusOptions}
          value={contractForm.watch('contractStatusId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('contractStatusId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.contractStatusId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.contractStatusId?.message
              ? translate(contractForm.errors.contractStatusId.message)
              : undefined
          }
          data-testid="contract-status-select"
        />

        <Selector
          label={translate('employee:contracts.fields.companyContract')}
          required
          options={companyOptions}
          value={contractForm.watch('companyContractId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('companyContractId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.companyContractId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.companyContractId?.message
              ? translate(contractForm.errors.companyContractId.message)
              : undefined
          }
          data-testid="company-contract-select"
        />

        <LabelInput
          label={translate('employee:contracts.fields.contractName')}
          required
          color={contractForm.errors.contractName ? 'error' : undefined}
          supportingText={
            contractForm.errors.contractName?.message
              ? translate(contractForm.errors.contractName.message)
              : undefined
          }
          value={normalizeWatchValueToString(contractForm.watch('contractName'))}
          onChange={(value: string) => contractForm.setValue('contractName', value, { shouldValidate: true, shouldDirty: true })}
          placeholder={translate('employee:contracts.fields.contractNamePlaceholder')}
          data-testid="contract-name-input"
        />

        <LabelInput
          label={translate('employee:contracts.fields.contractNumber')}
          required
          color={contractForm.errors.contractNumber ? 'error' : undefined}
          supportingText={
            contractForm.errors.contractNumber?.message
              ? translate(contractForm.errors.contractNumber.message)
              : undefined
          }
          value={normalizeWatchValueToString(contractForm.watch('contractNumber'))}
          onChange={(value: string) => contractForm.setValue('contractNumber', value, { shouldValidate: true, shouldDirty: true })}
          placeholder={translate('employee:contracts.fields.contractNumberPlaceholder')}
          data-testid="contract-number-input"
        />

        <Selector
          label={translate('employee:contracts.fields.contractType')}
          required
          options={contractTypeOptions}
          value={contractForm.watch('contractTypeId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeNumericSelectValue(value);
            contractForm.setValue('contractTypeId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.contractTypeId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.contractTypeId?.message
              ? translate(contractForm.errors.contractTypeId.message)
              : undefined
          }
          data-testid="contract-type-select"
        />

        <Selector
          label={translate('employee:contracts.fields.contractModality')}
          required
          options={contractModalityOptions}
          value={contractForm.watch('contractModalityId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeNumericSelectValue(value);
            contractForm.setValue('contractModalityId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.contractModalityId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.contractModalityId?.message
              ? translate(contractForm.errors.contractModalityId.message)
              : undefined
          }
          data-testid="contract-modality-select"
        />

        <DatePicker
          label={translate('employee:contracts.fields.dateEntry')}
          value={normalizeWatchValue(contractForm.watch('dateEntry'))}
          onChange={(date: string) => contractForm.setValue('dateEntry', date, { shouldValidate: true, shouldDirty: true })}
          color={contractForm.errors.dateEntry ? 'error' : undefined}
          supportingText={
            contractForm.errors.dateEntry?.message
              ? translate(contractForm.errors.dateEntry.message)
              : undefined
          }
          placeholder="dd/mm/aaaa"
          closeOnSelect
          required
        />

        <DatePicker
          label={translate('employee:contracts.fields.contractStartDate')}
          value={normalizeWatchValue(contractForm.watch('contractStartDate'))}
          onChange={(date: string) => contractForm.setValue('contractStartDate', date, { shouldValidate: true, shouldDirty: true })}
          color={contractForm.errors.contractStartDate ? 'error' : undefined}
          supportingText={
            contractForm.errors.contractStartDate?.message
              ? translate(contractForm.errors.contractStartDate.message)
              : undefined
          }
          placeholder="dd/mm/aaaa"
          closeOnSelect
          required
        />

        <DatePicker
          label={translate('employee:contracts.fields.contractEndDate')}
          value={normalizeWatchValue(contractForm.watch('contractEndDate'))}
          onChange={(date: string) => contractForm.setValue('contractEndDate', date, { shouldValidate: true, shouldDirty: true })}
          color={contractForm.errors.contractEndDate ? 'error' : undefined}
          supportingText={
            contractForm.errors.contractEndDate?.message
              ? translate(contractForm.errors.contractEndDate.message)
              : undefined
          }
          placeholder="dd/mm/aaaa"
          closeOnSelect
        />
      </FormLayout.Section>

      {/* Sección 2: Datos organizacionales */}
      <FormLayout.Section title="Datos organizacionales">
        <Selector
          label={translate('employee:contracts.fields.area')}
          required
          options={areaOptions}
          value={contractForm.watch('areaId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('areaId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.areaId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.areaId?.message
              ? translate(contractForm.errors.areaId.message)
              : undefined
          }
          data-testid="area-select"
        />

        <Selector
          label={translate('employee:contracts.fields.position')}
          required
          disabled={!contractForm.watch('areaId')}
          options={positionOptions}
          value={contractForm.watch('positionId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('positionId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.positionId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.positionId?.message
              ? translate(contractForm.errors.positionId.message)
              : undefined
          }
          data-testid="position-select"
        />

        <Selector
          label={translate('employee:contracts.fields.costCenter')}
          required
          options={costCenterOptions}
          value={contractForm.watch('costCenterId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('costCenterId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.costCenterId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.costCenterId?.message
              ? translate(contractForm.errors.costCenterId.message)
              : undefined
          }
          data-testid="cost-center-select"
        />

        <Selector
          label={translate('employee:contracts.fields.union')}
          required
          options={unionOptions}
          value={contractForm.watch('unionCode') || ''}
          onChange={(value: string | string[]) => {
            const stringValue = normalizeSelectValue(value);
            contractForm.setValue('unionCode', stringValue || '', { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.unionCode ? 'error' : 'default'}
          supportingText={
            contractForm.errors.unionCode?.message
              ? translate(contractForm.errors.unionCode.message)
              : undefined
          }
          data-testid="union-select"
        />
      </FormLayout.Section>

      {/* Action buttons */}
      <FormLayout.Footer
        cancelButton={
          <PrimaryButton type="button" variant="outline" onClick={onCancel} disabled={creating}>
            {translate('common:actions.cancel')}
          </PrimaryButton>
        }
        primaryButton={
          <PermissionGuard action="create">
            <PrimaryButton type="button" onClick={onSave} disabled={creating} isLoading={creating}>
              {translate('common:actions.save')}
            </PrimaryButton>
          </PermissionGuard>
        }
      />
    </FormLayout>
  );
};

export default CreateEmployeeContractForm;
